import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Typography } from '@material-tailwind/react';
import { PlusCircleIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

export const TabActas = ({ id_obrero, esVisualizacion }) => {
    const [actas, setActas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rangos, setRangos] = useState([]);
    const [selectedActa, setSelectedActa] = useState(null);
    
    const initialFormState = { id_obrero_acta: null, nro_acta: '', fecha: '', tipo: 'Alta', id_rango: '', observaciones: '' };
    const [form, setForm] = useState(initialFormState);

    useEffect(() => {
        if(id_obrero) {
            buscarActas(id_obrero);
            traerCursos();
        }
    }, [id_obrero]);

    useEffect(() => {
        if (selectedActa && selectedActa.id_obrero_acta !== form.id_obrero_acta) {
            abrirEditar(selectedActa.id_obrero_acta);
        }
    }, [selectedActa]);

    const buscarActas = async (id) => {
        setLoading(true);
        try {
            const { data } = await Axios.get(`/api/tablasgenerales/obreroactas/${id}`);
            setActas(data);
            if (data && data.length > 0 && !selectedActa) {
                setSelectedActa(data[0]);
                abrirEditar(data[0].id_obrero_acta);
            } else if (data.length === 0) {
                setForm(initialFormState);
                setSelectedActa(initialFormState);
            }
        } catch (err) {
            console.log('Error', err);
        } finally {
            setLoading(false);
        }
    };
    
    const traerCursos = async () => {
        try {
            const response = await Axios.get(`/api/tablasgenerales/listar-rangos`);
            setRangos(response.data);
        } catch (err) {}
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const formatearFechaParaInput = (fechaText) => {
        if (!fechaText) return '';
        if (fechaText.includes('-')) return fechaText.substring(0, 10);
        const partes = fechaText.split('/');
        if (partes.length === 3) return `${partes[2]}-${partes[1]}-${partes[0]}`;
        return fechaText;
    };

    const abrirNuevo = () => {
        setForm(initialFormState);
        setSelectedActa(initialFormState);
    };

    const abrirEditar = async (id) => {
        if (!id) return;
        try {
            const { data } = await Axios.get(`/api/tablasgenerales/obreroacta/${id}`);
            if(data && data.length > 0) {
                const actaEdit = data[0];
                let tipoDeterminado = 'Alta';
                if(actaEdit.ascenso) tipoDeterminado = 'Ascenso';
                else if(actaEdit.baja) tipoDeterminado = 'Baja';
                else if(actaEdit.otro) tipoDeterminado = 'Otro';

                const newForm = {
                    id_obrero_acta: actaEdit.id_obrero_acta,
                    nro_acta: actaEdit.nro_acta || '',
                    fecha: formatearFechaParaInput(actaEdit.fecha),
                    tipo: tipoDeterminado,
                    id_rango: actaEdit.id_rango || '',
                    observaciones: actaEdit.obs || actaEdit.observaciones || ''
                };
                setForm(newForm);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const borrarActa = (id, e) => {
        if (e) e.stopPropagation();
        if(esVisualizacion) return;
        Swal.fire({
            title: '¿Eliminar Acta?',
            text: "Esta acción no se puede deshacer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await Axios.delete(`/api/tablasgenerales/obreroacta/${id}`);
                    Swal.fire('Eliminado!', 'El acta fue eliminada.', 'success');
                    setSelectedActa(null);
                    setForm(initialFormState);
                    buscarActas(id_obrero);
                } catch (error) {
                    Swal.fire('Error', 'Ocurrió un error.', 'error');
                }
            }
        });
    };

    const guardarActa = async () => {
        let payload = {
            id_obrero_acta: form.id_obrero_acta,
            id_obrero: id_obrero,
            ascenso: form.tipo === 'Ascenso',
            alta: form.tipo === 'Alta',
            baja: form.tipo === 'Baja',
            otro: form.tipo === 'Otro',
            id_rango: form.id_rango ? Number(form.id_rango) : null,
            nro_acta: form.nro_acta ? Number(form.nro_acta) : 0,
            fecha: form.fecha || null,
            obs: form.observaciones
        };

        try {
            if (form.id_obrero_acta) {
                await Axios.put('/api/tablasgenerales/obreroacta', payload);
                Swal.fire('Actualizado', 'Acta actualizada', 'success');
            } else {
                await Axios.post('/api/tablasgenerales/obreroacta', payload);
                Swal.fire('Guardado', 'Acta creada', 'success');
            }
            buscarActas(id_obrero);
        } catch (error) {
            Swal.fire('Error', 'Ocurrió un error', 'error');
        }
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        try { return fecha.split('T')[0].split('-').reverse().join('/'); }
        catch (e) { return fecha; }
    };

    return (
        <div className="bg-white p-5 rounded-md text-sm w-full">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 w-full">
                {/* Left side: List box */}
                <div className="flex flex-col col-span-1 lg:col-span-5 border border-gray-300 bg-white shadow-sm h-full max-h-[500px] overflow-hidden">
                    <div className="bg-gray-100 border-b border-gray-300 flex justify-between items-center px-4 py-3 shrink-0">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Actas</span>
                        
                    </div>
                    <div className="bg-gray-50 border-b border-gray-200 flex flex-row items-center shrink-0">
                        <div className="w-16 md:w-20 px-2 py-2 text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">Nro</div>
                        <div className="w-20 md:w-24 px-2 py-2 text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">Fecha</div>
                        <div className="w-16 md:w-24 px-2 py-2 text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">Tipo</div>
                        <div className="flex-1 px-2 py-2 text-xs font-bold text-gray-600 truncate">Observaciones</div>
                    </div>
                    <ul className="flex-1 overflow-y-auto no-scrollbar">
                        {loading ? (
                            <div className="p-4 text-gray-400 text-xs text-center italic">Cargando...</div>
                        ) : actas && actas.length > 0 ? (
                            actas.map((item, idx) => (
                                <li 
                                    key={idx} 
                                    className={`px-2 py-3 cursor-pointer text-sm border-b border-gray-100 last:border-0 flex justify-between items-center transition-colors ${form.id_obrero_acta === item.id_obrero_acta ? 'bg-blue-50 text-blue-800' : 'text-gray-800 hover:bg-gray-50'}`}
                                    onClick={() => setSelectedActa(item)}
                                >
                                    <div className="flex flex-row w-full items-center text-center">
                                        <div className="w-16 md:w-20 truncate px-1 md:px-2 border-r border-gray-100">{item.nro_acta}</div>
                                        <div className="w-20 md:w-24 truncate px-1 md:px-2 border-r border-gray-100 text-[11px] md:text-sm">{formatFecha(item.fecha)}</div>
                                        <div className="w-16 md:w-24 truncate px-1 md:px-2 border-r border-gray-100 flex items-center justify-center">
                                            <span className="px-1 py-0.5 rounded-md bg-blue-50 text-blue-700 text-[9px] md:text-[10px] font-medium border border-blue-200 truncate max-w-full">
                                                {item.tipo || (item.ascenso ? 'Ascenso' : item.alta ? 'Alta' : item.baja ? 'Baja' : item.otro ? 'Otro' : '')}
                                            </span>
                                        </div>
                                        <div className="flex-1 truncate px-1 md:px-2 text-left text-[11px] md:text-sm" title={item.obs || item.observaciones}>{item.obs || item.observaciones || '-'}</div>
                                    </div>
                                    {!esVisualizacion && (
                                        <button 
                                            type="button"
                                            title="Eliminar acta"
                                            onClick={(e) => borrarActa(item.id_obrero_acta, e)}
                                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors ml-2"
                                        >
                                            <TrashIcon width={18} height={18} />
                                        </button>
                                    )}
                                </li>
                            ))
                        ) : (
                            <div className="p-4 text-gray-400 text-xs text-center flex flex-col items-center justify-center h-full italic">No hay actas registradas</div>
                        )}
                    </ul>
                </div>

                {/* Right side: Form */}
                <div className="flex flex-col col-span-1 lg:col-span-7 bg-gray-50 border border-gray-200 rounded-md p-5 shadow-sm overflow-y-auto max-h-[500px]">
                    <Typography variant="small" color="blue-gray" className="mb-4 font-semibold uppercase tracking-wider text-xs border-b border-gray-200 pb-2">
                        {form.id_obrero_acta ? 'Detalle del Acta' : 'Nueva Acta'}
                    </Typography>

                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-700">N° Acta</span>
                                <input type="text" name="nro_acta" disabled={esVisualizacion} className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full" value={form.nro_acta} onChange={handleFormChange} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-700">Fecha</span>
                                <input type="date" name="fecha" disabled={esVisualizacion} className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full" value={form.fecha} onChange={handleFormChange} />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-700">Tipo de acta</span>
                            <div className="flex flex-row flex-wrap gap-4 border border-gray-300 rounded-md bg-white p-3">
                                {['Alta', 'Ascenso', 'Baja', 'Otro'].map(tipo => (
                                    <label key={tipo} className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" disabled={esVisualizacion} name="tipo" value={tipo} checked={form.tipo === tipo} onChange={handleFormChange} className="w-4 h-4 text-blue-600 bg-white" />
                                        <span className="text-sm text-gray-700">{tipo}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-700">Rango</span>
                            <select name="id_rango" disabled={esVisualizacion} value={form.id_rango} onChange={handleFormChange} className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full">
                                <option value="">Seleccione rango...</option>
                                {rangos.map(r => (
                                    <option key={r.id_rango} value={r.id_rango}>{r.nombre}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-700">Observaciones</span>
                            <textarea name="observaciones" disabled={esVisualizacion} value={form.observaciones} onChange={handleFormChange} rows="3" className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full"></textarea>
                        </div>

                        {!esVisualizacion && (
                            <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
                                
                                <button
                                    type="button"
                                    onClick={abrirNuevo}
                                    className="text-red-400 bg-red-100 hover:bg-red-200 py-2 px-6 font-bold rounded-md shadow-sm transition-all text-sm uppercase mr-2"
                                    title="Limpiar selección / Nuevo"
                                >
                                    LIMPIAR FILTROS
                                </button>
                                <button type="button" onClick={guardarActa} className="flex items-center bg-blue-500 hover:bg-blue-600 text-white py-2 px-5 rounded-md text-sm font-bold transition-colors shadow-sm"><CheckIcon className="w-5 h-5 mr-1" strokeWidth={2} /> GUARDAR</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
